import { useMemo } from 'react';
import { Typography, Tag } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { usePaymentModeRender } from './usePaymentModeRender';
import { useUserTimestampRender } from '@src/hooks';
import {
  PAYMENT_TYPE_COLORS,
  PAYMENT_TYPE_FILTER_OPTIONS,
  PAYMENT_MODE_FILTER_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
} from '../constants';
import dayjs from 'dayjs';

const { Text } = Typography;

interface UsePaymentsColumnsOptions {
  showCreatedAt?: boolean;
}

export const usePaymentsColumns = (
  options: UsePaymentsColumnsOptions = {}
): SmartDataTableProps['columns'] => {
  const { showCreatedAt = true } = options;
  const renderPaymentMode = usePaymentModeRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  return useMemo(() => {
    const baseColumns: SmartDataTableProps['columns'] = [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'type',
        title: 'Payment Type',
        dataIndex: 'type',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'select' as const,
        filterOptions: [...PAYMENT_TYPE_FILTER_OPTIONS],
        render: (type: string) => {
          if (!type) return '-';
          const color =
            PAYMENT_TYPE_COLORS[type as keyof typeof PAYMENT_TYPE_COLORS] ||
            'default';
          
          // Find the label from PAYMENT_TYPE_OPTIONS
          const typeOption = PAYMENT_TYPE_OPTIONS.find(option => option.value === type);
          const displayText = typeOption ? typeOption.label : type?.replace('_', ' ');
          
          return (
            <Tag color={color} style={{ fontWeight: 500 }}>
              {displayText}
            </Tag>
          );
        },
      },
      {
        key: 'reference_id',
        title: 'Reference ID',
        dataIndex: 'reference_id',
        sorter: false,
        minWidth: 120,
        render: (value: any) => (
          <Text style={{ fontWeight: 500, color: '#262626' }}>
            {value ? `#${value}` : '-'}
          </Text>
        ),
      },
      {
        key: 'credit_amount',
        title: 'Credit',
        dataIndex: 'amount',
        sorter: false,
        minWidth: 120,
        align: 'right' as const,
        render: (amount: string, record: any) => {
          const isCredit = record.transaction_type === 'CREDIT';
          if (!isCredit) return <Text style={{ color: '#d9d9d9' }}>-</Text>;
          
          return (
            <Text style={{ fontWeight: 500, color: '#52c41a', paddingRight: '4px' }}>
              {amount
                ? Number(amount).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                  })
                : '0.00'}
            </Text>
          );
        },
      },
      {
        key: 'debit_amount',
        title: 'Debit',
        dataIndex: 'amount',
        sorter: false,
        minWidth: 120,
        align: 'right' as const,
        render: (amount: string, record: any) => {
          const isDebit = record.transaction_type === 'DEBIT';
          if (!isDebit) return <Text style={{ color: '#d9d9d9' }}>-</Text>;
          
          return (
            <Text style={{ fontWeight: 500, color: '#ff4d4f', paddingRight: '4px' }}>
              {amount
                ? Number(amount).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                  })
                : '0.00'}
            </Text>
          );
        },
      },
      {
        key: 'payment_mode',
        title: 'Payment Mode',
        dataIndex: 'payment_mode',
        sorter: false,
        minWidth: 130,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...PAYMENT_MODE_FILTER_OPTIONS],
        // filterDependsOn: 'type', // Payment mode filter depends on payment type
        render: renderPaymentMode,
      },
      {
        key: 'remark',
        title: 'Remark',
        dataIndex: 'remark',
        sorter: false,
        minWidth: 200,
        ellipsis: true,
        render: (remark: string) => (
          <Text style={{ color: '#666' }}>{remark || '-'}</Text>
        ),
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'created_at',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ];

    // Conditionally add the created_at column with date range filter
    if (showCreatedAt) {
      baseColumns.push({
        key: 'created_at',
        title: 'Created Date',
        dataIndex: 'created_at',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'daterange' as const,
        // filterDependsOn: 'type', // Created date filter depends on payment type
        render: () => null, // Hide the column content but keep filter functionality
        hidden: true, // Hide the column from display
      });
    }

    return baseColumns;
  }, [
    renderPaymentMode,
    userTimestampRender,
    showCreatedAt,
  ]);
};
